import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs, doc, getDoc, updateDoc, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { hasPermission, isStaffRole, PERMISSIONS } from '../utils/rolePermissions';
import '../styles/CandidateSubmissionPortal.css';

const CandidateSubmissionPortal = ({ candidateId, submissionYear, readOnly = false }) => {
  const { currentUser } = useAuth();
  const toast = useToast();

  // All hooks must be called before any early return (Rules of Hooks)
  const [candidate, setCandidate] = useState(null);
  const [template, setTemplate] = useState(null);
  const [submission, setSubmission] = useState({
    year: submissionYear || new Date().getFullYear(),
    sermonLinks: [{ title: '', url: '', description: '' }],
    disciplinaryQuestions: [],
    personalStatement: '',
    callStatement: '',
    customSections: {},
    additionalDocuments: [],
    submittedAt: null,
    status: 'draft'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCandidateAndSubmission();
  }, [candidateId, submissionYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Candidates can only access their own submission portal.
  // Staff roles (non-Candidate) may access any candidate's portal.
  if (currentUser && !currentUser.isAnonymous && !isStaffRole(currentUser.role) && currentUser.uid !== candidateId) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>
        <h3>Access Denied</h3>
        <p>You can only view your own submission portal.</p>
      </div>
    );
  }

  const fetchCandidateAndSubmission = async () => {
    try {
      const year = submissionYear || new Date().getFullYear();

      // Fetch candidate and existing submission in parallel
      const [candidateDoc, submissionSnap] = await Promise.all([
        getDoc(doc(db, 'candidates', candidateId)),
        getDocs(query(
          collection(db, 'candidateSubmissions'),
          where('candidateId', '==', candidateId),
          where('year', '==', year)
        )),
      ]);

      if (!candidateDoc.exists()) {
        console.error('Candidate not found');
        setLoading(false);
        return;
      }

      const candidateData = { id: candidateDoc.id, ...candidateDoc.data() };
      setCandidate(candidateData);

      // Fetch template after we know the candidate's conference status
      const candidateStatus = candidateData.conferenceStatus || candidateData.candidateType || 'local-pastor';
      const templateData = await fetchTemplate(candidateStatus);
      setTemplate(templateData);

      // Initialize questions from template
      const templateQuestions = templateData?.sections?.disciplinaryQuestions?.questions || [];
      const initialQuestions = templateQuestions
        .filter(q => q.enabled)
        .map(q => ({ question: q.question, answer: '' }));

      const snapshot = submissionSnap;
      
      if (!snapshot.empty) {
        const existingSubmission = snapshot.docs[0].data();
        
        // Merge existing answers with current template questions
        const mergedQuestions = initialQuestions.map(templateQ => {
          const existingQ = existingSubmission.disciplinaryQuestions?.find(
            eq => eq.question === templateQ.question
          );
          return existingQ || templateQ;
        });

        setSubmission({
          year: submissionYear || new Date().getFullYear(),
          sermonLinks: existingSubmission.sermonLinks || [{ title: '', url: '', description: '' }],
          disciplinaryQuestions: mergedQuestions,
          personalStatement: existingSubmission.personalStatement || '',
          callStatement: existingSubmission.callStatement || '',
          customSections: existingSubmission.customSections || {},
          additionalDocuments: existingSubmission.additionalDocuments || [],
          submittedAt: existingSubmission.submittedAt || null,
          status: existingSubmission.status || 'draft',
          id: snapshot.docs[0].id
        });
      } else {
        // No existing submission, use template defaults
        setSubmission({
          year: submissionYear || new Date().getFullYear(),
          sermonLinks: [{ title: '', url: '', description: '' }],
          disciplinaryQuestions: initialQuestions,
          personalStatement: '',
          callStatement: '',
          customSections: {},
          additionalDocuments: [],
          submittedAt: null,
          status: 'draft'
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplate = async (conferenceStatus) => {
    try {
      const templatesRef = collection(db, 'submissionTemplates');

      // Server-side filter: only fetch the template matching this status
      const matchSnap = await getDocs(
        query(templatesRef, where('conferenceStatus', '==', conferenceStatus))
      );

      let matchingTemplate = null;
      if (!matchSnap.empty) {
        const firstMatch = matchSnap.docs[0];
        matchingTemplate = { id: firstMatch.id, ...firstMatch.data() };
      }

      // Fallback: any template in the collection
      if (!matchingTemplate) {
        const fallbackSnap = await getDocs(query(templatesRef, limit(1)));
        if (!fallbackSnap.empty) {
          const firstDoc = fallbackSnap.docs[0];
          matchingTemplate = { id: firstDoc.id, ...firstDoc.data() };
          console.warn(`No template found for conference status "${conferenceStatus}", using default template`);
        }
      }

      // If still no template, create a default structure
      if (!matchingTemplate) {
        console.warn('No templates found in database, using fallback template');
        return {
          sections: {
            disciplinaryQuestions: {
              enabled: true,
              questions: [
                { id: 1, question: 'Have you studied the doctrines of The United Methodist Church?', enabled: true, required: true },
                { id: 2, question: 'After full examination do you believe that our doctrines are in harmony with the Holy Scriptures?', enabled: true, required: true },
                { id: 3, question: 'Will you preach and maintain them?', enabled: true, required: true },
                { id: 4, question: 'Have you studied our form of Church discipline and polity?', enabled: true, required: true },
                { id: 5, question: 'Do you approve our Church government and polity?', enabled: true, required: true },
                { id: 6, question: 'Will you support and maintain them?', enabled: true, required: true },
                { id: 7, question: 'Will you diligently instruct the children in every place?', enabled: true, required: true },
                { id: 8, question: 'Will you visit from house to house?', enabled: true, required: true },
                { id: 9, question: 'Will you recommend fasting or abstinence, both by precept and example?', enabled: true, required: true }
              ]
            },
            sermonLinks: { enabled: true },
            personalStatement: { enabled: true },
            callStatement: { enabled: true }
          }
        };
      }

      return matchingTemplate;
    } catch (error) {
      console.error('Error fetching template:', error);
      // Return fallback template
      return {
        sections: {
          disciplinaryQuestions: {
            enabled: true,
            questions: [
              { id: 1, question: 'Have you studied the doctrines of The United Methodist Church?', enabled: true, required: true },
              { id: 2, question: 'After full examination do you believe that our doctrines are in harmony with the Holy Scriptures?', enabled: true, required: true },
              { id: 3, question: 'Will you preach and maintain them?', enabled: true, required: true },
              { id: 4, question: 'Have you studied our form of Church discipline and polity?', enabled: true, required: true },
              { id: 5, question: 'Do you approve our Church government and polity?', enabled: true, required: true },
              { id: 6, question: 'Will you support and maintain them?', enabled: true, required: true },
              { id: 7, question: 'Will you diligently instruct the children in every place?', enabled: true, required: true },
              { id: 8, question: 'Will you visit from house to house?', enabled: true, required: true },
              { id: 9, question: 'Will you recommend fasting or abstinence, both by precept and example?', enabled: true, required: true }
            ]
          },
          sermonLinks: { enabled: true },
          personalStatement: { enabled: true },
          callStatement: { enabled: true }
        }
      };
    }
  };

  const handleSermonLinkChange = (index, field, value) => {
    const updatedLinks = [...submission.sermonLinks];
    updatedLinks[index][field] = value;
    setSubmission({ ...submission, sermonLinks: updatedLinks });
  };

  const addSermonLink = () => {
    setSubmission({
      ...submission,
      sermonLinks: [...submission.sermonLinks, { title: '', url: '', description: '' }]
    });
  };

  const removeSermonLink = (index) => {
    const updatedLinks = submission.sermonLinks.filter((_, i) => i !== index);
    setSubmission({ ...submission, sermonLinks: updatedLinks });
  };

  const handleDisciplinaryAnswer = (index, answer) => {
    const updatedQuestions = [...submission.disciplinaryQuestions];
    updatedQuestions[index].answer = answer;
    setSubmission({ ...submission, disciplinaryQuestions: updatedQuestions });
  };

  const handleCustomSectionChange = (sectionId, value) => {
    setSubmission({
      ...submission,
      customSections: {
        ...submission.customSections,
        [sectionId]: value
      }
    });
  };

  const handleDownload = () => {
    // Create a formatted text version of the submission
    let content = `ANNUAL SUBMISSION REPORT\n`;
    content += `${'='.repeat(80)}\n\n`;
    content += `Candidate: ${candidate.firstName} ${candidate.lastName}\n`;
    content += `Submission Year: ${submission.year}\n`;
    content += `Status: ${submission.status}\n`;
    content += `Submitted: ${submission.submittedAt ? new Date(submission.submittedAt.seconds * 1000).toLocaleString() : 'Draft'}\n`;
    content += `\n${'='.repeat(80)}\n\n`;

    // Sermon Links
    if (template?.sections?.sermonLinks?.enabled && submission.sermonLinks.length > 0) {
      content += `SERMON LINKS\n${'-'.repeat(80)}\n\n`;
      submission.sermonLinks.forEach((link, index) => {
        if (link.title || link.url) {
          content += `${index + 1}. ${link.title || 'Untitled'}\n`;
          content += `   URL: ${link.url || 'No URL provided'}\n`;
          if (link.description) {
            content += `   Description: ${link.description}\n`;
          }
          content += `\n`;
        }
      });
      content += `\n`;
    }

    // Disciplinary Questions
    if (template?.sections?.disciplinaryQuestions?.enabled && submission.disciplinaryQuestions.length > 0) {
      content += `DISCIPLINARY QUESTIONS\n${'-'.repeat(80)}\n\n`;
      submission.disciplinaryQuestions.forEach((item, index) => {
        content += `${index + 1}. ${item.question}\n`;
        content += `   Answer: ${item.answer || 'No answer provided'}\n\n`;
      });
      content += `\n`;
    }

    // Personal Statement
    if (template?.sections?.personalStatement?.enabled && submission.personalStatement) {
      content += `PERSONAL STATEMENT\n${'-'.repeat(80)}\n\n`;
      content += `${submission.personalStatement}\n\n`;
    }

    // Call Statement
    if (template?.sections?.callStatement?.enabled && submission.callStatement) {
      content += `CALL STATEMENT\n${'-'.repeat(80)}\n\n`;
      content += `${submission.callStatement}\n\n`;
    }

    // Custom Sections
    if (template?.sections?.additionalSections && template.sections.additionalSections.length > 0) {
      template.sections.additionalSections.forEach(section => {
        if (section.enabled && submission.customSections[section.id]) {
          content += `${section.title.toUpperCase()}\n${'-'.repeat(80)}\n\n`;
          const value = submission.customSections[section.id];
          if (Array.isArray(value)) {
            content += `${value.join(', ')}\n\n`;
          } else {
            content += `${value}\n\n`;
          }
        }
      });
    }

    content += `\n${'='.repeat(80)}\n`;
    content += `End of Report\n`;

    // Create and download file
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${candidate.lastName}_${candidate.firstName}_Submission_${submission.year}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const submissionData = {
        ...submission,
        candidateId,
        candidateName: `${candidate.firstName} ${candidate.lastName}`,
        lastUpdated: new Date(),
        status: 'draft'
      };

      if (submission.id) {
        await updateDoc(doc(db, 'candidateSubmissions', submission.id), submissionData);
      } else {
        const docRef = await addDoc(collection(db, 'candidateSubmissions'), submissionData);
        setSubmission({ ...submission, id: docRef.id });
      }

      toast.success('Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Error saving draft. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const hasSermons = submission.sermonLinks.some(link => link.title && link.url);
    const hasAnswers = submission.disciplinaryQuestions.every(q => q.answer.trim());
    
    if (!hasSermons) {
      toast.warning('Please provide at least one sermon link.');
      return;
    }

    if (!hasAnswers) {
      toast.warning('Please answer all disciplinary questions.');
      return;
    }

    if (!submission.personalStatement.trim() || !submission.callStatement.trim()) {
      toast.warning('Please complete your personal statement and call statement.');
      return;
    }

    setSaving(true);
    try {
      const submissionData = {
        ...submission,
        candidateId,
        candidateName: `${candidate.firstName} ${candidate.lastName}`,
        submittedAt: new Date(),
        lastUpdated: new Date(),
        status: 'submitted'
      };

      if (submission.id) {
        await updateDoc(doc(db, 'candidateSubmissions', submission.id), submissionData);
      } else {
        await addDoc(collection(db, 'candidateSubmissions'), submissionData);
      }

      toast.success('Submission completed successfully!');
      setSubmission({ ...submission, status: 'submitted', submittedAt: new Date() });
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Error submitting. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="submission-portal">
        <div className="loading-spinner">Loading submission portal...</div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="submission-portal">
        <div className="error-message">Candidate not found.</div>
      </div>
    );
  }

  const isSubmitted = submission.status === 'submitted';
  
  // Check if current user is an admin who can edit submissions
  const canAdminEdit = currentUser && (
    hasPermission(currentUser, PERMISSIONS.MANAGE_ALL_CANDIDATES) ||
    hasPermission(currentUser, PERMISSIONS.MANAGE_CONFERENCE_CANDIDATES) ||
    hasPermission(currentUser, PERMISSIONS.MANAGE_DISTRICT_CANDIDATES)
  );
  
  // Determine if fields should be disabled
  // Admins can always edit, otherwise respect readOnly and submission status
  const isDisabled = canAdminEdit ? false : (readOnly || isSubmitted);

  return (
    <div className="submission-portal">
      <div className="portal-header">
        <div className="portal-header-content">
          <div>
            <h1>{template?.title || 'Annual Submission Portal'}</h1>
            <div className="candidate-info">
              <h2>{candidate.firstName} {candidate.lastName}</h2>
              <p>Submission Year: {submission.year}</p>
              <div className={`status-badge ${submission.status}`}>
                {submission.status === 'submitted' ? 'Submitted' : 'Draft'}
              </div>
              {canAdminEdit && isSubmitted && (
                <div className="admin-edit-badge" title="You have admin permissions to edit this submission">
                  🔓 Admin Edit Mode
                </div>
              )}
            </div>
            {template?.description && (
              <p className="portal-description">{template.description}</p>
            )}
          </div>
          {isSubmitted && (
            <button 
              onClick={handleDownload} 
              className="download-btn"
              title="Download submission as text file"
            >
              📥 Download Submission
            </button>
          )}
        </div>
      </div>

      <div className="submission-form">
        {/* Sermon Links Section */}
        {template?.sections?.sermonLinks?.enabled && (
          <div className="form-section">
            <h3>{template.sections.sermonLinks.title || 'Sermon Links'}</h3>
            <p className="section-description">
              {template.sections.sermonLinks.description || 'Please provide links to your sermons. Include the title, URL, and a brief description of each sermon.'}
            </p>
          
          {submission.sermonLinks.map((link, index) => (
            <div key={index} className="sermon-link-group">
              <div className="form-row">
                <div className="form-group">
                  <label>Sermon Title</label>
                  <input
                    type="text"
                    value={link.title}
                    onChange={(e) => handleSermonLinkChange(index, 'title', e.target.value)}
                    placeholder="e.g., Grace in Times of Trial"
                    disabled={isDisabled}
                  />
                </div>
                <div className="form-group">
                  <label>URL/Link</label>
                  {isDisabled && link.url ? (
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="sermon-url-link"
                    >
                      {link.url}
                    </a>
                  ) : (
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => handleSermonLinkChange(index, 'url', e.target.value)}
                      placeholder="https://..."
                      disabled={isDisabled}
                    />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={link.description}
                  onChange={(e) => handleSermonLinkChange(index, 'description', e.target.value)}
                  placeholder="Brief description of the sermon content, context, and key themes..."
                  rows="3"
                  disabled={isDisabled}
                />
              </div>
              {!isDisabled && submission.sermonLinks.length > 1 && (
                <button 
                  type="button" 
                  onClick={() => removeSermonLink(index)}
                  className="remove-btn"
                >
                  Remove Sermon
                </button>
              )}
            </div>
          ))}
          
          {!isDisabled && (
            <button type="button" onClick={addSermonLink} className="add-btn">
              Add Another Sermon
            </button>
          )}
          </div>
        )}

        {/* Disciplinary Questions Section */}
        {template?.sections?.disciplinaryQuestions?.enabled && (
          <div className="form-section">
            <h3>{template.sections.disciplinaryQuestions.title || 'Disciplinary Questions'}</h3>
            <p className="section-description">
              {template.sections.disciplinaryQuestions.description || 'Please answer the following questions from The Book of Discipline.'}
            </p>
          
          {submission.disciplinaryQuestions.map((item, index) => (
            <div key={index} className="question-group">
              <label className="question-label">
                {index + 1}. {item.question}
              </label>
              <textarea
                value={item.answer}
                onChange={(e) => handleDisciplinaryAnswer(index, e.target.value)}
                placeholder="Please provide your answer..."
                rows="4"
                disabled={isDisabled}
                required
              />
            </div>
          ))}
          </div>
        )}

        {/* Personal Statement Section */}
        {template?.sections?.personalStatement?.enabled && (
          <div className="form-section question-group">
            <h3>{template.sections.personalStatement.title || 'Personal Statement'}</h3>
          <p className="section-description">
            {template.sections.personalStatement.description || 'Please provide a personal statement reflecting on your spiritual journey and growth this year.'}
          </p>

          <textarea
            value={submission.personalStatement}
            onChange={(e) => setSubmission({ ...submission, personalStatement: e.target.value })}
            placeholder={template.sections.personalStatement.placeholder || 'Share your spiritual journey, growth experiences, challenges, and insights from this year...'}
            rows="8"
            disabled={isDisabled}
            required
          />
          </div>
        )}

        {/* Call Statement Section */}
        {template?.sections?.callStatement?.enabled && (
          <div className="form-section question-group">
            <h3>{template.sections.callStatement.title || 'Call Statement'}</h3>
            <p className="section-description">
              {template.sections.callStatement.description || 'Please describe your understanding of your call to ministry and how it has evolved.'}
            </p>
            <textarea
              value={submission.callStatement}
              onChange={(e) => setSubmission({ ...submission, callStatement: e.target.value })}
              placeholder={template.sections.callStatement.placeholder || 'Describe your call to ministry, how it has developed, and your sense of direction...'}
              rows="8"
              disabled={isDisabled}
              required
            />
          </div>
        )}

        {/* Custom Sections */}
        {template?.sections?.additionalSections?.map((section) => {
          if (!section.enabled) return null;
          
          return (
            <div key={section.id} className="form-section question-group">
              <h3>{section.title}</h3>
              {section.description && (
                <p className="section-description">{section.description}</p>
              )}
              
              {section.type === 'text' && (
                <input
                  type="text"
                  value={submission.customSections[section.id] || ''}
                  onChange={(e) => handleCustomSectionChange(section.id, e.target.value)}
                  placeholder={section.placeholder || ''}
                  disabled={isDisabled}
                  required={section.required}
                />
              )}
              
              {section.type === 'textarea' && (
                <textarea
                  value={submission.customSections[section.id] || ''}
                  onChange={(e) => handleCustomSectionChange(section.id, e.target.value)}
                  placeholder={section.placeholder || ''}
                  rows="6"
                  disabled={isDisabled}
                  required={section.required}
                />
              )}
              
              {section.type === 'select' && (
                <select
                  value={submission.customSections[section.id] || ''}
                  onChange={(e) => handleCustomSectionChange(section.id, e.target.value)}
                  disabled={isDisabled}
                  required={section.required}
                >
                  <option value="">Select an option...</option>
                  {section.options?.map((option, index) => (
                    <option key={index} value={option}>{option}</option>
                  ))}
                </select>
              )}
              
              {section.type === 'radio' && (
                <div className="radio-group">
                  {section.options?.map((option, index) => (
                    <label key={index} className="radio-label">
                      <input
                        type="radio"
                        name={`custom-${section.id}`}
                        value={option}
                        checked={submission.customSections[section.id] === option}
                        onChange={(e) => handleCustomSectionChange(section.id, e.target.value)}
                        disabled={isDisabled}
                        required={section.required}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              )}
              
              {section.type === 'checkbox' && (
                <div className="checkbox-group">
                  {section.options?.map((option, index) => {
                    const currentValues = submission.customSections[section.id] || [];
                    const isChecked = Array.isArray(currentValues) && currentValues.includes(option);
                    
                    return (
                      <label key={index} className="checkbox-label">
                        <input
                          type="checkbox"
                          value={option}
                          checked={isChecked}
                          onChange={(e) => {
                            const values = Array.isArray(currentValues) ? [...currentValues] : [];
                            if (e.target.checked) {
                              values.push(option);
                            } else {
                              const idx = values.indexOf(option);
                              if (idx > -1) values.splice(idx, 1);
                            }
                            handleCustomSectionChange(section.id, values);
                          }}
                          disabled={isDisabled}
                        />
                        {option}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Submission Actions */}
        {!readOnly && (
          <div className="form-actions">
            {!isSubmitted ? (
              <>
                <button 
                  type="button" 
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="save-draft-btn"
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button 
                  type="button" 
                  onClick={handleSubmit}
                  disabled={saving}
                  className="submit-btn"
                >
                  {saving ? 'Submitting...' : 'Submit for Review'}
                </button>
              </>
            ) : canAdminEdit ? (
              <>
                <div className="submitted-info">
                  <p>✅ Submitted on {new Date(submission.submittedAt.seconds * 1000).toLocaleDateString()}</p>
                  <p>Your submission has been received and will be reviewed by the dCOM.</p>
                </div>
                <button 
                  type="button" 
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="save-draft-btn admin-save-btn"
                  title="Save changes to this submitted entry (Admin only)"
                >
                  {saving ? 'Saving Changes...' : '💾 Save Admin Changes'}
                </button>
              </>
            ) : (
              <div className="submitted-info">
                <p>✅ Submitted on {new Date(submission.submittedAt.seconds * 1000).toLocaleDateString()}</p>
                <p>Your submission has been received and will be reviewed by the dCOM.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateSubmissionPortal;
